import type { FaceKey } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { useStore } from 'zustand';
import {
  type CompositeAssetKind,
  compositeAssetOf,
  compositeSlotsOf,
  useEditAreaDraftStore,
} from '@/entities/edit-area';
import { COMPOSITE_ASSET_KINDS } from '../model/composite-asset-kinds';
import { FACE_LABELS } from '../model/face-labels';
import { useCompositeAssetUpload } from '../model/use-composite-asset-upload';
import { AssetUploadControls } from './asset-upload-controls';

interface CompositeAssetSlotProps {
  readonly face: FaceKey;
  readonly kind: CompositeAssetKind;
  /** 마스크가 없는 면의 음영·안내선 — 마스크를 먼저 올려야 해서 업로드를 잠근다. */
  readonly disabled?: boolean;
}

/** 면 합성 에셋 한 종류의 칸 — 미리보기·업로드·재시도·취소·삭제. 업로드 조작은 사진 카드와 같은 조각(AssetUploadControls)이다. */
export const CompositeAssetSlot = ({
  face,
  kind,
  disabled = false,
}: CompositeAssetSlotProps): ReactElement => {
  const store = useEditAreaDraftStore();
  const spec = COMPOSITE_ASSET_KINDS[kind];
  const composite = useStore(store, (state) => state.faces[face]?.composite);
  const asset = compositeAssetOf(composite, kind);
  const url = useStore(store, (state) => (asset ? state.photoUrls[asset.key] : undefined));
  const { state, upload, retry, cancel } = useCompositeAssetUpload(face, kind);
  const label = `${FACE_LABELS[face]} ${spec.label}`;
  const remove = (): void => {
    if (kind === 'mask') {
      // 마스크는 필수라 지우면 그 면 합성 설정 전체와 진행 중인 합성 업로드가 함께 빠진다(Q7·D-E).
      const pending = compositeSlotsOf(store.getState().operations, face).length;
      const cancelNotice = pending > 0 ? ` 진행 중인 업로드 ${pending}개도 취소됩니다.` : '';
      if (
        !window.confirm(
          `마스크를 지우면 ${FACE_LABELS[face]}의 음영·안내선·곱하기 설정도 함께 빠집니다. 삭제하시겠습니까?${cancelNotice}`,
        )
      )
        return;
    }
    store.getState().removeCompositeAsset(face, kind);
  };

  return (
    <div className="min-w-0 rounded border p-2">
      <p className="text-sm font-medium">
        {spec.label}{' '}
        <span className="text-xs font-normal text-muted-foreground">{spec.description}</span>
      </p>
      <AssetUploadControls
        label={label}
        subject={spec.label}
        accept={spec.accept}
        preview={asset ? { url, alt: label, size: asset.size } : null}
        emptyText="미등록"
        compact
        disabled={disabled}
        state={state}
        onUpload={(file) => void upload(file)}
        onRetry={() => void retry()}
        onCancel={cancel}
        onRemove={remove}
      />
    </div>
  );
};
